import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useAuth from "@/lib/auth/useAuth";
import { useMemo } from "react";
import { COLOR_MAP } from "@/types/color";
import {
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { roundTo } from "@/lib/utils/numbers";
import useRequest from "@/lib/request/useRequest";
import { DSSATSimulation } from "@/types/dssat";
import dayjs from "dayjs";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { Spinner, RangeValue, DateValue, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { Resource } from "@/types/user";
import { CropCycleV2 } from "@/types/cropCycle";

function NUptake({ dateRange }: { dateRange: RangeValue<DateValue> | null }) {
    const { t } = useTranslation("insights");
    const { formatDateMMDDYYYY } = useI18n();
    const { fieldId, selectedCropCycle } = useFieldDashboard() ?? {};
    const { useGetRequest } = useRequest();
    const { isAtLeastDetail } = useAuth();

    const growthModelDetail = isAtLeastDetail(Resource.GROWTH_MODEL);
    const dssatSimulationsQuery = useGetRequest<DSSATSimulation[]>({
        url: `/fields/${fieldId}/dssat_simulations/`,
        enabled: !!fieldId,
    });
    const data = useMemo(() => {
        return sortArrayByKey(
            dssatSimulationsQuery.data?.filter((d) => {
                const date = dayjs(d.date);
                return (
                    d.crop_cycle === (selectedCropCycle as CropCycleV2).id &&
                    (date.isSame(dateRange?.start?.toString(), "day") ||
                        date.isAfter(dateRange?.start?.toString())) &&
                    (date.isSame(dateRange?.end?.toString()) || date.isBefore(dateRange?.end?.toString()))
                );
            }) ?? [],
            "date",
        ).map((row) => ({
            ...row,
            date: dayjs(row.date).toDate(),
            [t("chartLabels.nitrogenApplied")]: row.nitrogen_applied,
            [t("chartLabels.nitrogenUptake")]: row.nitrogen_uptake,
            [t("chartLabels.nitrogenLeached")]: row.nitrogen_leached,
            [t("chartLabels.waterStress")]: growthModelDetail
                ? row.water_stress
                : undefined,
            [t("chartLabels.nitrogenStress")]: growthModelDetail
                ? row.nitrogen_stress
                : undefined,
            [t("chartLabels.leafNumber")]: growthModelDetail
                ? row.leaf_number
                : undefined,
            [t("chartLabels.canopyWeight")]: growthModelDetail
                ? row.canopy_weight
                : undefined,
            [t("chartLabels.rootDensity5cm")]: growthModelDetail
                ? row.root_density_5cm
                : undefined,
            [t("chartLabels.rootDensity15cm")]: growthModelDetail
                ? row.root_density_15cm
                : undefined,
            [t("chartLabels.rootDensity30cm")]: growthModelDetail
                ? row.root_density_30cm
                : undefined,
            [t("chartLabels.rootDensity60cm")]: growthModelDetail
                ? row.root_density_60cm
                : undefined,
            [t("chartLabels.rootDensity100cm")]: growthModelDetail
                ? row.root_density_100cm
                : undefined,
            [t("chartLabels.rootDensity200cm")]: growthModelDetail
                ? row.root_density_200cm
                : undefined,
            [t("chartLabels.soilMoisture5cm")]: growthModelDetail
                ? row.soil_moisutre_5cm
                : undefined,
            [t("chartLabels.soilMoisture15cm")]: growthModelDetail
                ? row.soil_moisutre_15cm
                : undefined,
            [t("chartLabels.soilMoisture30cm")]: growthModelDetail
                ? row.soil_moisutre_30cm
                : undefined,
            [t("chartLabels.soilMoisture60cm")]: growthModelDetail
                ? row.soil_moisutre_60cm
                : undefined,
            [t("chartLabels.soilMoisture100cm")]: growthModelDetail
                ? row.soil_moisutre_100cm
                : undefined,
            [t("chartLabels.soilMoisture200cm")]: growthModelDetail
                ? row.soil_moisutre_200cm
                : undefined,
            [t("chartLabels.ammonium5cm")]: growthModelDetail
                ? row.ammonium_5cm
                : undefined,
            [t("chartLabels.ammonium15cm")]: growthModelDetail
                ? row.ammonium_15cm
                : undefined,
            [t("chartLabels.ammonium30cm")]: growthModelDetail
                ? row.ammonium_30cm
                : undefined,
            [t("chartLabels.ammonium60cm")]: growthModelDetail
                ? row.ammonium_60cm
                : undefined,
            [t("chartLabels.ammonium100cm")]: growthModelDetail
                ? row.ammonium_100cm
                : undefined,
            [t("chartLabels.ammonium200cm")]: growthModelDetail
                ? row.ammonium_200cm
                : undefined,
            [t("chartLabels.nitrate5cm")]: growthModelDetail
                ? row.nitrate_5cm
                : undefined,
            [t("chartLabels.nitrate15cm")]: growthModelDetail
                ? row.nitrate_15cm
                : undefined,
            [t("chartLabels.nitrate30cm")]: growthModelDetail
                ? row.nitrate_30cm
                : undefined,
            [t("chartLabels.nitrate60cm")]: growthModelDetail
                ? row.nitrate_60cm
                : undefined,
            [t("chartLabels.nitrate100cm")]: growthModelDetail
                ? row.nitrate_100cm
                : undefined,
            [t("chartLabels.nitrate200cm")]: growthModelDetail
                ? row.nitrate_200cm
                : undefined,
            [t("chartLabels.soilNitrogen5cm")]: growthModelDetail
                ? Number(row.ammonium_5cm ?? 0) + Number(row.nitrate_5cm ?? 0)
                : undefined,
            [t("chartLabels.soilNitrogen15cm")]: growthModelDetail
                ? Number(row.ammonium_15cm ?? 0) + Number(row.nitrate_15cm ?? 0)
                : undefined,
            [t("chartLabels.soilNitrogen30cm")]: growthModelDetail
                ? Number(row.ammonium_30cm ?? 0) + Number(row.nitrate_30cm ?? 0)
                : undefined,
            [t("chartLabels.soilNitrogen60cm")]: growthModelDetail
                ? Number(row.ammonium_60cm ?? 0) + Number(row.nitrate_60cm ?? 0)
                : undefined,
            [t("chartLabels.soilNitrogen100cm")]: growthModelDetail
                ? Number(row.ammonium_100cm ?? 0) +
                Number(row.nitrate_100cm ?? 0)
                : undefined,
            [t("chartLabels.soilNitrogen200cm")]: growthModelDetail
                ? Number(row.ammonium_200cm ?? 0) +
                Number(row.nitrate_200cm ?? 0)
                : undefined,
        }));
    }, [
        dssatSimulationsQuery.data,
        dateRange,
        t,
        growthModelDetail,
        selectedCropCycle,
    ]);

    const max = useMemo(() => {
        return Math.max(
            ...data.map((d) => Number(d.nitrogen_uptake)),
            ...data.map((d) => Number(d.nitrogen_applied)),
            ...data.map((d) => Number(d.nitrogen_leached)),
        );
    }, [data]);

    // const maxSoilMoisture = useMemo(() => {
    //     if (!growthModelDetail) return 0;
    //     return (
    //         Math.max(
    //             ...data
    //                 .filter((d) => d.soil_moisutre_5cm !== null)
    //                 .map((d) => Number(d.soil_moisutre_5cm)),
    //             ...data
    //                 .filter((d) => d.soil_moisutre_5cm !== null)
    //                 .map((d) => Number(d.soil_moisutre_15cm)),
    //             ...data
    //                 .filter((d) => d.soil_moisutre_5cm !== null)
    //                 .map((d) => Number(d.soil_moisutre_30cm)),
    //             ...data
    //                 .filter((d) => d.soil_moisutre_5cm !== null)
    //                 .map((d) => Number(d.soil_moisutre_60cm)),
    //             ...data
    //                 .filter((d) => d.soil_moisutre_5cm !== null)
    //                 .map((d) => Number(d.soil_moisutre_100cm)),
    //             ...data
    //                 .filter((d) => d.soil_moisutre_5cm !== null)
    //                 .map((d) => Number(d.soil_moisutre_200cm)),
    //         ) + 0.1
    //     );
    // }, [data, growthModelDetail]);

    const maxSoilNitrogen = useMemo(() => {
        if (!growthModelDetail) return 0;
        const soilNitrogen5cm = data.map(
            (d) =>
                (Number(d.ammonium_5cm ?? 0) + Number(d.nitrate_5cm ?? 0)) as number,
        );
        const soilNitrogen15cm = data.map(
            (d) =>
                (Number(d.ammonium_15cm ?? 0) + Number(d.nitrate_15cm ?? 0)) as number,
        );
        const soilNitrogen30cm = data.map(
            (d) =>
                (Number(d.ammonium_30cm ?? 0) + Number(d.nitrate_30cm ?? 0)) as number,
        );
        const soilNitrogen60cm = data.map(
            (d) =>
                (Number(d.ammonium_60cm ?? 0) + Number(d.nitrate_60cm ?? 0)) as number,
        );
        const soilNitrogen100cm = data.map(
            (d) =>
                (Number(d.ammonium_100cm ?? 0) +
                    Number(d.nitrate_100cm ?? 0)) as number,
        );
        const soilNitrogen200cm = data.map(
            (d) =>
                (Number(d.ammonium_200cm ?? 0) +
                    Number(d.nitrate_200cm ?? 0)) as number,
        );
        return (
            Math.max(
                ...soilNitrogen5cm,
                ...soilNitrogen15cm,
                ...soilNitrogen30cm,
                ...soilNitrogen60cm,
                ...soilNitrogen100cm,
                ...soilNitrogen200cm,
            ) + 0.1
        ).toFixed();
    }, [data, growthModelDetail]);

    return (
        <div className="flex flex-col gap-4">
            <div className="font-semibold text-sm">
                {t("chartLabels.nitrogenDynamics")}
            </div>
            <ResponsiveContainer width="100%" height={400}>
                {dssatSimulationsQuery.isLoading ? (
                    <Spinner label={t("messages.loadingData")} />
                ) : !data.length ? (
                    <div className="bg-gray-100 h-full rounded-sm">
                        <div className=" p-3 italic text-center">
                            {t("messages.noDataSelectedDates")}
                        </div>
                    </div>
                ) : (
                    <LineChart data={data} margin={{ bottom: 16, left: 8 }}>
                        <XAxis
                            dataKey="date"
                            scale="time"
                            spacing={50}
                            angle={-8}
                            tickFormatter={(t) => formatDateMMDDYYYY(t)}
                        />
                        <YAxis
                            label={{
                                value: t("chartLabels.nitrogenLbNAc"),
                                angle: -90,
                                position: "left",
                                dx: 10,
                            }}
                            domain={[0, max]}
                        />
                        <Tooltip
                            labelFormatter={(t) => formatDateMMDDYYYY(t)}
                            formatter={(value, name) => [
                                `${roundTo(value as number, 1)}`,
                                name,
                            ]}
                        />
                        <Legend />

                        <Line
                            dataKey={t("chartLabels.nitrogenUptake")}
                            stroke={COLOR_MAP[9]}
                            dot={false}
                        />
                        <Line
                            dataKey={t("chartLabels.nitrogenApplied")}
                            stroke={COLOR_MAP[7]}
                            dot={false}
                        />
                        <Line
                            dataKey={t("chartLabels.nitrogenLeached")}
                            stroke={COLOR_MAP[6]}
                            dot={false}
                        />
                        {growthModelDetail && (
                            <Line
                                dataKey={t("chartLabels.nitrogenStress")}
                                stroke={COLOR_MAP[0]}
                                dot={false}
                            />
                        )}
                    </LineChart>
                )}
            </ResponsiveContainer>

            {growthModelDetail && (
                <>
                    {/* <div className="text-lg font-bold">
                        {t("chartLabels.soilMoisture")}
                    </div>
                    <ResponsiveContainer width="100%" height={400}>
                        {dssatSimulationsQuery.isLoading ? (
                            <Spinner label={t("messages.loadingData")} />
                        ) : !data.length ? (
                            <div className="bg-gray-100 h-full rounded-sm">
                                <div className=" p-3 italic text-center">
                                    {t("messages.noDataSelectedDates")}
                                </div>
                            </div>
                        ) : (
                            <LineChart
                                data={data}
                                margin={{ bottom: 16, left: 8 }}
                            >
                                <XAxis
                                    dataKey="date"
                                    scale="time"
                                    spacing={50}
                                    angle={-8}
                                    tickFormatter={(t) =>
                                        dayjs(t).format("MM/DD/YY")
                                    }
                                />
                                <YAxis
                                    label={{
                                        value: t(
                                            "chartLabels.soilMoisturecm3-cm3",
                                        ),
                                        angle: -90,
                                        position: "left",
                                        dx: 10,
                                    }}
                                    domain={[0, maxSoilMoisture]}
                                />
                                <Tooltip
                                    labelFormatter={(t) =>
                                        dayjs(t).format("MM/DD/YY")
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)}`,
                                        name,
                                    ]}
                                />
                                <Legend />
                                <Line
                                    dataKey={t("chartLabels.soilMoisture5cm")}
                                    stroke="#8B4513"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilMoisture15cm")}
                                    stroke="#A0522D"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilMoisture30cm")}
                                    stroke="#D2691E"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilMoisture60cm")}
                                    stroke="#FF7F50"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilMoisture100cm")}
                                    stroke="#FF8C00"
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilMoisture200cm")}
                                    stroke="#FFA500"
                                    dot={false}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer> */}
                    <Divider />
                    <div className="font-semibold text-sm">
                        {t("chartLabels.soilNitrogen")}
                    </div>
                    <ResponsiveContainer width="100%" height={400}>
                        {dssatSimulationsQuery.isLoading ? (
                            <Spinner label={t("messages.loadingData")} />
                        ) : !data.length ? (
                            <div className="bg-gray-100 h-full rounded-sm">
                                <div className=" p-3 italic text-center">
                                    {t("messages.noDataSelectedDates")}
                                </div>
                            </div>
                        ) : (
                            <LineChart
                                data={data}
                                margin={{ bottom: 16, left: 8 }}
                            >
                                <XAxis
                                    dataKey="date"
                                    scale="time"
                                    spacing={50}
                                    angle={-8}
                                    tickFormatter={(t) =>
                                        dayjs(t).format("MM/DD/YY")
                                    }
                                />
                                <YAxis
                                    label={{
                                        value: t("chartLabels.soilNitrogenPpm"),
                                        angle: -90,
                                        position: "left",
                                        dx: 10,
                                    }}
                                    domain={[0, maxSoilNitrogen]}
                                />
                                <Tooltip
                                    labelFormatter={(t) =>
                                        dayjs(t).format("MM/DD/YY")
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)}`,
                                        name,
                                    ]}
                                />
                                <Legend />
                                <Line
                                    dataKey={t("chartLabels.soilNitrogen5cm")}
                                    stroke={COLOR_MAP[0]}
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilNitrogen15cm")}
                                    stroke={COLOR_MAP[1]}
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilNitrogen30cm")}
                                    stroke={COLOR_MAP[2]}
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilNitrogen60cm")}
                                    stroke={COLOR_MAP[3]}
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilNitrogen100cm")}
                                    stroke={COLOR_MAP[4]}
                                    dot={false}
                                />
                                <Line
                                    dataKey={t("chartLabels.soilNitrogen200cm")}
                                    stroke={COLOR_MAP[5]}
                                    dot={false}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                </>
            )}
        </div>
    );
}

export default NUptake;
